// <OrgSortDropdown> — Org View Step 8 (T-054, spec §6.1.1).
//
// Custom dropdown trigger styled like a small ghost button:
//   "Sort: <current> ▾"
// Options:
//   - Alphabetical (A→Z)         — default
//   - Start date (newest first)
//   - Team (alphabetical)
//
// Persists per user via localStorage (key `org.sort`). Closes on
// outside click, Esc, and after selection. Portal-rendered so the
// dropdown can escape any ancestor `overflow:hidden`.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ChevronDown } from 'lucide-react';

export type OrgSortKey = 'alpha' | 'startDate' | 'team';

const SORT_LABEL: Record<OrgSortKey, string> = {
  alpha: 'Alphabetical (A→Z)',
  startDate: 'Start date (newest first)',
  team: 'Team (A→Z)',
};

const SORT_LABEL_SHORT: Record<OrgSortKey, string> = {
  alpha: 'A→Z',
  startDate: 'Newest first',
  team: 'Team',
};

export interface OrgSortDropdownProps {
  value: OrgSortKey;
  onChange: (next: OrgSortKey) => void;
}

export function OrgSortDropdown({ value, onChange }: OrgSortDropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="org-sort-trigger"
        className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider px-3 py-2 rounded-lg hover:bg-slate-100"
      >
        <ArrowUpDown size={14} aria-hidden />
        <span>Sort: {SORT_LABEL_SHORT[value]}</span>
        <ChevronDown
          size={14}
          aria-hidden
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="menu"
              data-testid="org-sort-panel"
              style={{
                position: 'fixed',
                top: position.top,
                right: position.right,
                zIndex: 9999,
              }}
              className="w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2"
            >
              {(Object.keys(SORT_LABEL) as OrgSortKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={value === key}
                  onClick={() => {
                    onChange(key);
                    setOpen(false);
                  }}
                  data-testid={`org-sort-option-${key}`}
                  data-selected={value === key}
                  className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors ${
                    value === key
                      ? 'text-[#6344C6] bg-[#6344C6]/5'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {SORT_LABEL[key]}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

// Pure helper exposed for unit testing.
export function sortEmployees<
  T extends { name: string; teamName: string | null; startDate: string },
>(employees: T[], sort: OrgSortKey): T[] {
  const out = employees.slice();
  switch (sort) {
    case 'startDate':
      // Newest first.
      out.sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0));
      break;
    case 'team':
      out.sort((a, b) => {
        const at = a.teamName ?? '';
        const bt = b.teamName ?? '';
        if (at !== bt) return at.localeCompare(bt);
        return a.name.localeCompare(b.name);
      });
      break;
    case 'alpha':
    default:
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
  }
  return out;
}
